import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from '@mui/lab';
import { Typography } from '@mui/material';
import { DynamicIcon } from 'src/components';
import { Schedule } from 'src/graphql/entities/stripe-subscription-details';
import {
  addZeroTime,
  formatDateToLocalString,
  getAmount,
  OPTIONS_LOCALE,
  SubscriptionSchedulePhaseType,
} from './subscription-details.utils';

interface Props {
  schedule?: Schedule;
  paused?: boolean;
  resumesAt?: string | null;
}

function shouldAddTimelineConnector(index: number, schedule?: Schedule) {
  const phases = schedule?.phases;
  if (!phases?.length) return false;

  const isLastPhase = index === phases.length - 1;
  const scheduleCancels = schedule?.cancelsAt;

  return isLastPhase ? Boolean(scheduleCancels) : true;
}

function getStartDate(
  index: number,
  startDate: string,
  opts?: { paused?: boolean; resumesAt?: string | null },
) {
  if (index === 0) {
    if (opts?.paused && opts.resumesAt) {
      return formatDateToLocalString({ date: opts.resumesAt, options: OPTIONS_LOCALE });
    }
    return 'Today';
  }
  return formatDateToLocalString({
    date: addZeroTime(startDate),
    options: OPTIONS_LOCALE,
  });
}

const timelineSx = {
  marginTop: 0,
  [`& .${timelineItemClasses.root}:before`]: {
    flex: 0,
    padding: 0,
  },
};

export const SubscriptionScheduleTimeline = ({ schedule, paused, resumesAt }: Props) => {
  if (!schedule?.phases?.length && !paused) return null;

  return (
    <Timeline sx={timelineSx}>
      {paused ? (
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot
              variant="outlined"
              sx={{
                border: 'none',
                height: '12px',
                width: '12px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DynamicIcon name="circle-pause" library="lucide" size={24} />
            </TimelineDot>
            {schedule?.phases?.length || schedule?.cancelsAt ? <TimelineConnector /> : null}
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="inherit" fontWeight="bold">
              {SubscriptionSchedulePhaseType.paused}
            </Typography>
            {resumesAt ? (
              <Typography variant="inherit" fontSize="0.8rem">
                Resumes on{' '}
                {formatDateToLocalString({
                  date: resumesAt,
                  options: OPTIONS_LOCALE,
                })}
              </Typography>
            ) : null}
          </TimelineContent>
        </TimelineItem>
      ) : null}
      {schedule?.phases?.map((phase, index) => (
        <TimelineItem key={phase.startDate}>
          <TimelineSeparator>
            <TimelineDot />
            {shouldAddTimelineConnector(index, schedule) && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            {phase.startDate ? (
              <Typography variant="inherit" fontWeight="bold">
                {getStartDate(index, phase.startDate, { paused, resumesAt })}
              </Typography>
            ) : null}
            {phase.phaseType ? (
              <Typography variant="inherit" fontSize="0.8rem">
                Phase:{' '}
                {
                  SubscriptionSchedulePhaseType[
                    phase.phaseType as keyof typeof SubscriptionSchedulePhaseType
                  ]
                }
              </Typography>
            ) : null}
            {phase.product ? (
              <Typography variant="inherit" fontSize="0.8rem">
                Product: {phase.product}
              </Typography>
            ) : null}
            {phase.priceInCents || phase.priceInCents === 0 ? (
              <Typography variant="inherit" fontSize="0.8rem">
                Price: {getAmount(phase.priceInCents)}
              </Typography>
            ) : null}
            {phase.coupon ? (
              <Typography variant="inherit" fontSize="0.8rem">
                Coupon: {phase.coupon}
              </Typography>
            ) : null}
          </TimelineContent>
        </TimelineItem>
      ))}
      {schedule?.cancelsAt ? (
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot
              variant="outlined"
              sx={{
                border: 'none',
                height: '12px',
                width: '12px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DynamicIcon name="x" library="lucide" size={24} />
            </TimelineDot>
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="inherit" fontWeight="bold">
              {formatDateToLocalString({
                date: addZeroTime(schedule.cancelsAt),
                options: OPTIONS_LOCALE,
              })}
            </Typography>
            <Typography variant="inherit" fontSize="0.75rem">
              Subscription cancels
            </Typography>
          </TimelineContent>
        </TimelineItem>
      ) : null}
    </Timeline>
  );
};
